import React, { type SFC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { DispatchTimezone } from "./DispatchTimezoneFormatters";
import type { ICampaignContext } from "src/campaigns/objects/CampaignQueryParam";
import { DispatchTypeFormatter } from "src/dispatches/formatters/DispatchTypeFormatter";
import type { Dispatch } from "src/dispatches/objects/Dispatch";
import { DispatchStatus } from "src/dispatches/objects/DispatchStatus";
import type {
  DispatchStatusQueryParam,
  IDispatchStatusContext,
} from "src/dispatches/objects/DispatchStatusQueryParam";
import {
  DISPATCHES_VIEW_PATH,
  GetDispatchDetailViewPath,
  GetDispatchesViewPath,
} from "src/dispatches/views/DispatchPaths";
import {
  type IRaangeLabelProps,
  type IRaangeLabelWithFAProps,
  RaangeLabel,
} from "src/shared/components/RaangeLabel";
import FormattedLocalDateTime from "src/shared/formatters/FormattedLocalDateTime";
import type ILinkable from "src/shared/formatters/ILinkable";

export const DispatchesTypeLabel: SFC<
  ILinkable &
    Omit<IRaangeLabelProps, "fa"> & { children: DispatchStatusQueryParam }
> = (props) => {
  const { children, ...rest } = props;
  if (children === "ACTIVE") {
    return <ActiveDispatchesLabel {...rest} />;
  } else if (children === "HISTORY") {
    return <HistoryDispatchesLabel {...rest} />;
  } else if (children === "DRAFT") {
    return <DraftsDispatchesLabel {...rest} />;
  } else {
    return null;
  }
};

export const ActiveDispatchesLabel: SFC<
  Omit<IRaangeLabelProps, "fa"> & ILinkable
> = (props) => {
  const { hasLink = false } = props;
  const label = (
    <RaangeLabel fa="sync" {...props}>
      <FormattedMessage id="dispatches.active" />
    </RaangeLabel>
  );
  if (hasLink) {
    return <Link to={GetDispatchesViewPath("ACTIVE")}>{label}</Link>;
  } else {
    return label;
  }
};

export const HistoryDispatchesLabel: SFC<
  Omit<IRaangeLabelProps, "fa"> & ILinkable
> = (props) => {
  const { hasLink = false } = props;
  const label = (
    <RaangeLabel fa="history" {...props}>
      <FormattedMessage id="dispatches.history" />
    </RaangeLabel>
  );
  if (hasLink) {
    return <Link to={GetDispatchesViewPath("HISTORY")}>{label}</Link>;
  } else {
    return label;
  }
};

export const DraftsDispatchesLabel: SFC<
  Omit<IRaangeLabelProps, "fa"> & ILinkable
> = (props) => {
  const { hasLink = false } = props;
  const label = (
    <RaangeLabel fa="pencil-alt" {...props}>
      <FormattedMessage id="dispatches.drafts" />
    </RaangeLabel>
  );
  if (hasLink) {
    return <Link to={GetDispatchesViewPath("DRAFT")}>{label}</Link>;
  } else {
    return label;
  }
};

export const DispatchesLabel = (props: IRaangeLabelWithFAProps & ILinkable) => {
  const { hasLink = false } = props;
  const label = (
    <RaangeLabel fa="pencil-alt" {...props}>
      <FormattedMessage id="dispatches" />
    </RaangeLabel>
  );
  if (hasLink) {
    return <Link to={DISPATCHES_VIEW_PATH}>{label}</Link>;
  } else {
    return label;
  }
};

export interface IDispatchFormatterProps extends Omit<IRaangeLabelProps, "fa"> {
  children: Dispatch;
}

export interface IDispatchNameProps
  extends IDispatchFormatterProps,
    ILinkable {}

export const DispatchName: SFC<
  IDispatchNameProps & IDispatchStatusContext & ICampaignContext
> = (props) => {
  const {
    children: dispatch,
    hasLink = false,
    dispatchStatus,
    campaign,
    ...rest
  } = props;
  const dispatchName = (
    <RaangeLabel fa="paper-plane" {...rest}>
      {dispatch.dispatchName}
    </RaangeLabel>
  );
  if (hasLink) {
    return (
      <Link
        to={GetDispatchDetailViewPath(
          dispatch.dispatchUUID,
          dispatchStatus,
          campaign,
        )}
      >
        {dispatchName}
      </Link>
    );
  } else {
    return dispatchName;
  }
};

export const DispatchTypeForDispatch: SFC<IDispatchFormatterProps> = (
  props,
) => {
  const { children: dispatch } = props;
  return <DispatchTypeFormatter>{dispatch.dispatchType}</DispatchTypeFormatter>;
};

export const DispatchStartTime: SFC<IDispatchFormatterProps> = (props) => {
  const { children: dispatch } = props;
  return <FormattedLocalDateTime value={dispatch.dispatchStartTime} />;
};

export const DispatchTimezoneForDispatch: SFC<IDispatchFormatterProps> = (
  props,
) => {
  const { children: dispatch } = props;
  return <DispatchTimezone>{dispatch.tzBasedOn}</DispatchTimezone>;
};

interface IDispatchStatusFormatter extends IDispatchFormatterProps {
  showsText?: boolean;
}

export const DispatchStatusFormatter = ({
  children: dispatch,
  showsText = true,
}: IDispatchStatusFormatter) => {
  const intl = useIntl();
  switch (dispatch.dispatchStatus) {
    case DispatchStatus.DRAFT: {
      const title = intl.formatMessage({ id: "dispatches.status.draft" });
      return (
        <span>
          <span className="fas fa-pencil-alt text-secondary" title={title} />{" "}
          {showsText && title}
        </span>
      );
    }
    case DispatchStatus.SCHEDULED: {
      const title = intl.formatMessage({ id: "dispatches.status.scheduled" });
      return (
        <span>
          <span className="fas fa-clock text-primary" title={title} />{" "}
          {showsText && title}
        </span>
      );
    }
    case DispatchStatus.RUNNING: {
      const title = intl.formatMessage({ id: "dispatches.status.running" });
      return (
        <span>
          <span className="fas fa-sync fa-spin text-primary" title={title} />{" "}
          {showsText && title}
        </span>
      );
    }
    case DispatchStatus.COMPLETED: {
      const title = intl.formatMessage({ id: "dispatches.status.completed" });
      return (
        <span>
          <span className="fas fa-check-circle text-success" title={title} />{" "}
          {showsText && title}
        </span>
      );
    }
    case DispatchStatus.PAUSED: {
      const title = intl.formatMessage({ id: "dispatches.status.paused" });
      return (
        <span>
          <span className="fas fa-pause text-secondary" title={title} />{" "}
          {showsText && title}
        </span>
      );
    }
    case DispatchStatus.PARTIALLY_CANCELLED: {
      const title = intl.formatMessage({
        id: "dispatches.status.partially_cancelled",
      });
      return (
        <span>
          <span className="fa-stack" title={title}>
            <span className="fas fa-check fa-stack-1x text-secondary" />
            <span className="fas fa-ban fa-stack-1x text-danger" />
          </span>{" "}
          {showsText && title}
        </span>
      );
    }
    case DispatchStatus.CANCELLED: {
      const title = intl.formatMessage({ id: "dispatches.status.cancelled" });
      return (
        <span>
          <span className="fas fa-ban text-danger" title={title} />{" "}
          {showsText && title}
        </span>
      );
    }
    case DispatchStatus.PENDING_APPROVAL: {
      const title = intl.formatMessage({
        id: "dispatches.status.pending_approval",
      });
      return (
        <span>
          <span className="fas fa-clock text-warning" title={title} />{" "}
          {showsText && title}
        </span>
      );
    }
    default: {
      const title = intl.formatMessage({ id: "dispatches.status.unknown" });
      return (
        <span>
          <span className="fas fa-question-square" title={title} />{" "}
          {showsText && title}
        </span>
      );
    }
  }
};

export const DispatchCreate = (props: IRaangeLabelWithFAProps) => {
  return (
    <RaangeLabel fa="plus" {...props}>
      <FormattedMessage id="dispatches.create" />
    </RaangeLabel>
  );
};

export const DispatchEdit = (props: IRaangeLabelWithFAProps) => {
  return (
    <RaangeLabel fa="pencil-alt" {...props}>
      <FormattedMessage id="dispatches.edit" />
    </RaangeLabel>
  );
};

export const DispatchCopy = (props: IRaangeLabelWithFAProps) => {
  return (
    <RaangeLabel fa="pencil-alt" {...props}>
      <FormattedMessage id="dispatches.copy" />
    </RaangeLabel>
  );
};
